import { styled, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import { Can } from "components/can";
import { ACTION_LIST } from "models/role/action";
import { SUBJECT_CIRCUIT_BREAKER } from "models/role/subject";

import { BLOCK_RULE_TYPE_BANK } from "../../constants";
import { BlocklistTypeIcon } from "../BlocklistTypeIcon";

import type { Bank } from "modules/banks";

import type { RuleResult as RulesResultType } from "../../models/RiskOverview";

export interface RuleResultProps {
  isSameCustomer: boolean;
  rule: RulesResultType;
  bank?: Pick<Bank, "branchName" | "id">;
  "data-testid"?: string;
}

const BASE_URL = "/circuit-breaker/blocklist";

export function RuleResult({
  rule,
  bank,
  isSameCustomer,
  ...props
}: RuleResultProps) {
  const { type, value } = rule;

  const params = new URLSearchParams();

  params.append("search", rule.id);
  params.append("active", "yes,no");

  const valueText =
    type === BLOCK_RULE_TYPE_BANK && bank?.id === value
      ? bank.branchName
      : value;

  const content = (
    <Container
      data-testid={props["data-testid"] ? `${props["data-testid"]}` : undefined}
    >
      <IconWrapper>
        <BlocklistTypeIcon type={type} />
      </IconWrapper>
      <div>
        <Typography sx={{ wordBreak: "break-word" }} variant="small">
          {valueText}
        </Typography>
      </div>
    </Container>
  );

  return (
    <>
      <Can do={ACTION_LIST} on={SUBJECT_CIRCUIT_BREAKER}>
        {isSameCustomer ? (
          <StyledLink
            to={`${BASE_URL}?${params.toString()}`}
            data-testid={
              props["data-testid"] ? `${props["data-testid"]}-link` : undefined
            }
          >
            {content}
          </StyledLink>
        ) : (
          content
        )}
      </Can>
      <Can do={ACTION_LIST} on={SUBJECT_CIRCUIT_BREAKER} not>
        {content}
      </Can>
    </>
  );
}

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  marginTop: theme.spacing(2),
  color: theme.palette.failure.main,
}));

const IconWrapper = styled("div")(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  display: "flex",
  marginRight: theme.spacing(1),
  width: "24px",
  height: "24px",
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",

  "&:hover": {
    textDecoration: "underline",
    textDecorationColor: theme.palette.failure.main,
  },
}));
